import React, { useEffect, useState } from 'react';
import { initTeams, getTeamsContext, getHostClientType, requestLocation, getSsoToken } from './services/teams.js';
import { markAttendance, getMyDay, setCanonicalMark } from './services/api.js';
import MarcacionPanel from './components/MarcacionPanel.jsx';
import HistorialHoy from './components/HistorialHoy.jsx';

export default function App() {
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState(null);
  const [hostClientType, setHostClientType] = useState(null);
  const [myDay, setMyDay] = useState({ ingresos: [], salidas: [], member: null });
  const [error, setError] = useState(null);
  const [ssoError, setSsoError] = useState(null);

  useEffect(() => {
    // Guardia dura contra pantalla en blanco: pase lo que pase, en 15s
    // salimos de "Inicializando" y mostramos lo que se pueda.
    const hardGuard = setTimeout(() => setLoading(false), 15000);

    (async () => {
      const inTeams = await initTeams();

      if (inTeams) {
        try {
          const [ctx, host] = await Promise.all([
            getTeamsContext(),
            getHostClientType()
          ]);
          setContext(ctx);
          setHostClientType(host);
        } catch (err) {
          console.warn('Teams context/host no disponible:', err);
        }

        try {
          const tok = await getSsoToken();
          if (!tok) {
            setSsoError('SSO no devolvio token. Cierra y reabre Teams, o avisa a TI.');
          }
        } catch (ssoErr) {
          console.error('SSO error:', ssoErr);
          setSsoError(ssoErr?.message || String(ssoErr));
        }
      } else {
        // No estamos en Teams (o Teams no respondio). Modo standalone.
        setHostClientType('standalone');
        setError('No detectamos Teams. Si estas dentro de Teams, cierra y reabre la app. Fuera de Teams, algunas funciones (SSO) no estan disponibles.');
      }

      try {
        const day = await getMyDay();
        setMyDay(day);
      } catch (dayErr) {
        console.warn('getMyDay fallo:', dayErr);
      }

      clearTimeout(hardGuard);
      setLoading(false);
    })();

    return () => clearTimeout(hardGuard);
  }, []);

  const refreshDay = async () => {
    try {
      const day = await getMyDay();
      setMyDay(day);
    } catch (err) {
      console.error('refreshDay error', err);
    }
  };

  const handleMark = async ({ tipo, comentario, onProgress }) => {
    const notify = typeof onProgress === 'function' ? onProgress : () => {};
    const location = await requestLocation(notify); // LocationError se propaga con kind + hint
    notify('saving');
    const payload = {
      tipo,
      comentario,
      host_client_type: hostClientType,
      latitude: location.latitude,
      longitude: location.longitude
    };
    const result = await markAttendance(payload);
    notify('refreshing');
    await refreshDay();
    return result;
  };

  const handleCanonical = async (mark_id) => {
    await setCanonicalMark(mark_id);
    await refreshDay();
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner-line" />
        <div className="lo-label">Inicializando</div>
      </div>
    );
  }

  const nombresCompletos = (myDay?.member?.nombres || '').trim();
  const apellidosCompletos = (myDay?.member?.apellidos || '').trim();
  const fullName = [nombresCompletos, apellidosCompletos].filter(Boolean).join(' ');
  const dni = (myDay?.member?.dni || '').trim();

  const firstName =
    nombresCompletos.split(' ')[0] ||
    context?.user?.userPrincipalName?.split('@')[0] ||
    'colaborador';
  const lastName = apellidosCompletos.split(' ')[0] || '';
  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  const saludo = [firstName, lastName].filter(Boolean).join(' ');

  const ingresoCanon = (myDay.ingresos || []).find((m) => m.is_canonical);
  const salidaCanon = (myDay.salidas || []).find((m) => m.is_canonical);
  const tardanzaHoy = ingresoCanon?.tardanza_minutos || 0;

  return (
    <div className="app">
      <div className="topbar">
        <div className="brandmark">
          <div className="brandmark-title">
            Asistencia<span className="dot">·</span>Manantial Tecnológico
          </div>
          <div className="brandmark-ruc">RUC 20537818612</div>
        </div>
        <div className="initials">{initials || '—'}</div>
      </div>

      <header className="hero hero-compact">
        <h1 className="hero-name">
          Hola, <em>{saludo}</em>
        </h1>
        <div className="hero-id">
          {dni ? (
            <span className="hero-id-dni">DNI {dni}</span>
          ) : (
            <span className="hero-id-dni missing">DNI no registrado — pide a RH que lo cargue</span>
          )}
        </div>
      </header>

      {error && (
        <div className="alert error">
          <span className="alert-tag">Error</span>
          <span>{error}</span>
        </div>
      )}
      {ssoError && (
        <div className="alert error">
          <span className="alert-tag">SSO</span>
          <span>{ssoError}</span>
        </div>
      )}

      <section className="section">
        <h2 className="section-label">Hoy</h2>
        <div className="today-line">
          {ingresoCanon ? (
            <>
              Ingreso a las <strong>{formatHora(ingresoCanon.timestamp_iso)}</strong>
              <span className="em-dash">—</span>
              {tardanzaHoy === 0
                ? <span className="puntual">puntual</span>
                : <span className="late">{tardanzaHoy} min tarde</span>}
            </>
          ) : (
            <>Sin marca de ingreso todavia.</>
          )}
          {salidaCanon && (
            <>
              {' · '}salida a las <strong>{formatHora(salidaCanon.timestamp_iso)}</strong>
            </>
          )}
        </div>
      </section>

      <MarcacionPanel
        myDay={myDay}
        onMark={handleMark}
        disabled={Boolean(ssoError)}
      />

      <HistorialHoy
        myDay={myDay}
        onSelectCanonical={handleCanonical}
      />

      <footer className="legal-footer">
        <div className="legal-line">
          Manantial Tecnológico E.I.R.L. · RUC 20537818612
        </div>
        {(fullName || dni) && (
          <div className="legal-line">
            {fullName || 'Colaborador'}{dni ? ` · DNI ${dni}` : ''}
          </div>
        )}
        <div className="device-tag">desde {hostClientType || 'web'}</div>
      </footer>
    </div>
  );
}

function formatHora(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
}
